<template>
  <ul v-bind="$attrs">
    <side-nav-menu-item :key="menuItem.routeName" v-for="menuItem in activeMenuItems" :menuItem="menuItem" @click="closeMenu" />
  </ul>
</template>

<script>
import { kebabCase } from 'lodash'

import { context, shortcut, menu } from '@/mixins/helpers'

import SideNavMenuItem from './SideNavMenuItem'

const buildMenuItem = (route) => {
  const { menu, permissions, shortcut } = route.meta

  // If the route has child items, check for sub menu items
  if (route.children) {
    const links = route.children.filter(route => route.meta && route.meta.menu).reduce((items, route) => { items.push(buildMenuItem(route)); return items }, [])

    if (links.length > 0) {
      Object.assign(menu, { links })
    }
  }

  // If the route has permissions, copy them to the menu item
  if (permissions) {
    Object.assign(menu, { permissions })
  }

  // If the route has a shortcut, copy them to the menu item
  if (shortcut) {
    Object.assign(menu, { shortcut })
  }

  return { identifier: kebabCase(menu.routeName || route.path.replace(/[\W_]+/g, '')), ...menu }
}

export default {
  name: 'SideNavMenu',

  mixins: [ context, shortcut, menu ],

  components: { SideNavMenuItem },

  data () {
    return {
      menuItems: [],
      activeMenuItems: [],
      activeShortcuts: []
    }
  },

  created () {
    /**
     * Build up the menu
     *
     * routeName: Vue router name
     * activeWhen: [] of Vue router names on which this menu has to be active
     * title: Display title
     * icon: Font Awesome icon
     * active: Active state in menu (if false no white color)
     */
    this.$router.options.routes.forEach(route => {
      if (route.meta && route.meta.menu) {
        this.menuItems.push(buildMenuItem(route))
      } else if (route.children) {
        route.children.filter(route => route.meta && route.meta.menu).forEach(childRoute => {
          this.menuItems.push(buildMenuItem(childRoute))
        })
      }
    })
  },

  mounted () {
    /**
     * Set active menu items
     */
    this.setActiveMenuItems()

    /**
     * Update active menu items after get permissions
     */
    this.$eventHub.$on('updateMenuItems', this.setActiveMenuItems)
  },

  methods: {
    /**
     * Show menu based on user permissions
     */
    setActiveMenuItems () {
      this.activeMenuItems = [...this.menuItems.filter(item => {
        const parameters = {
          context: this.context,
          policy: this.$store.state.authorization.policy,
          business: this.context.isSelected('business') ? this.$store.getters.business(this.context.business.id) : null,
          merchant: this.context.isSelected('merchant') ? this.$store.getters.merchant(this.context.merchant.id) : null
        }

        let canShowItem = this.$hasPermissions(item.permissions || []) && (item.canShow ? item.canShow(parameters) : true)

        if (canShowItem && item.links) {
          item.activeLinks = item.links.filter(link => this.$hasPermissions(link.permissions || []) && (link.canShow ? link.canShow(parameters) : true))
          canShowItem = item.activeLinks.length > 0
        }

        return canShowItem
      })].sort((a, b) => a.index - b.index)

      this.getShortcuts()
    },

    getShortcuts () {
      // Unbind active shortcuts
      this.activeShortcuts.forEach(({ combinations }) => this.unbind({ combinations }))

      const shortcuts = []

      this.activeMenuItems.forEach(menuItem => {
        if (menuItem.shortcut && this.hasSelectedContext(menuItem)) {
          shortcuts.push({ combinations: menuItem.shortcut, route: this.getRoute(menuItem) })
        }

        if (menuItem.activeLinks) {
          menuItem.activeLinks.filter(link => link.shortcut).forEach(link => this.hasSelectedContext(link) && shortcuts.push({ combinations: link.shortcut, route: this.getRoute(link) }))
        }
      })

      this.activeShortcuts = shortcuts

      // Bind active shortcuts
      this.activeShortcuts.forEach(({ combinations, route }) => this.bind({ combinations, handler: () => this.$router.push(route) }))
    },

    // Duplicate, need to remove with composition api rewrite
    hasSelectedContext (link) {
      return typeof link.hasSelectedContext !== 'undefined' ? link.hasSelectedContext.every(key => this.context.isSelected(key)) : true
    },

    // Duplicate, need to remove with composition api rewrite
    getRoute (link) {
      const route = { name: link.routeName }

      if (link.routeParams) {
        Object.assign(route, { params: link.routeParams(this.context) })
      }

      return route
    }
  }
}
</script>
